<template>
  <div class="panel-marketing">
    <div class="panel-tabs-wrap mb-4 w-100">
      <ul class="nav nav-tabs mb-0">
        <li class="nav-item">
          <button type="button" class="nav-link" :class="{ active: tab === 'coupons' }" @click="tab = 'coupons'">
            <i class="fas fa-ticket-alt me-2"></i>Cupones
          </button>
        </li>
        <li class="nav-item">
          <button type="button" class="nav-link" :class="{ active: tab === 'offers' }" @click="tab = 'offers'">
            <i class="fas fa-percent me-2"></i>Ofertas especiales
          </button>
        </li>
        <li class="nav-item">
          <button type="button" class="nav-link" :class="{ active: tab === 'shipping' }" @click="tab = 'shipping'">
            <i class="fas fa-truck me-2"></i>Envío gratis
          </button>
        </li>
      </ul>
    </div>

    <!-- Cupones -->
    <div v-if="tab === 'coupons'">
      <div class="panel-toolbar">
        <p class="panel-toolbar-meta">Códigos de descuento para pedidos en catálogo</p>
        <div class="panel-toolbar-actions">
          <button class="btn btn-primary" @click="openCouponModal()">
            <i class="fas fa-plus me-2"></i>Nuevo cupón
          </button>
        </div>
      </div>

      <div v-if="loadingCoupons" class="text-center py-5">
        <div class="spinner-border text-primary"></div>
      </div>
      <template v-else>
        <div class="panel-mobile-cards d-md-none">
          <article v-for="c in coupons" :key="`m-c-${c.id}`" class="panel-data-card">
            <header class="panel-data-card__head">
              <div>
                <p class="panel-data-card__title mb-0"><code>{{ c.code }}</code></p>
                <div class="panel-data-card__subtitle">{{ couponTypeLabel(c.type) }}</div>
              </div>
              <span class="badge" :class="couponBadgeClass(c)">{{ couponStatusLabel(c) }}</span>
            </header>
            <div class="panel-data-card__rows">
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Valor</span>
                <span class="panel-data-card__value">
                  {{ c.type === 'percentage' ? c.value + '%' : c.type === 'fixed' ? formatPrice(c.value) : '—' }}
                </span>
              </div>
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Roles</span>
                <span class="panel-data-card__value">{{ rolesLabel(c) }}</span>
              </div>
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Usos</span>
                <span class="panel-data-card__value">{{ c.used }}{{ c.max_uses ? ' / ' + c.max_uses : '' }}</span>
              </div>
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Vence</span>
                <span class="panel-data-card__value">{{ formatDate(c.expires_at) }}</span>
              </div>
            </div>
            <footer class="panel-data-card__actions">
              <button class="btn btn-sm btn-outline-light" @click="openCouponModal(c)"><i class="fas fa-pen me-1"></i>Editar</button>
              <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteCoupon(c)"><i class="fas fa-trash me-1"></i>Eliminar</button>
            </footer>
          </article>
          <div v-if="coupons.length === 0" class="panel-data-card text-center text-muted py-4">No hay cupones creados</div>
        </div>
        <div class="table-responsive panel-table-wrap d-none d-md-block">
          <table class="table table-dark table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Código</th>
                <th>Tipo</th>
                <th>Valor</th>
                <th>Roles</th>
                <th>Usos</th>
                <th>Vence</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in coupons" :key="c.id">
                <td><code>{{ c.code }}</code></td>
                <td>{{ couponTypeLabel(c.type) }}</td>
                <td>{{ c.type === 'percentage' ? c.value + '%' : c.type === 'fixed' ? formatPrice(c.value) : '—' }}</td>
                <td><small>{{ rolesLabel(c) }}</small></td>
                <td>{{ c.used }}{{ c.max_uses ? ' / ' + c.max_uses : '' }}</td>
                <td>{{ formatDate(c.expires_at) }}</td>
                <td>
                  <span class="badge" :class="couponBadgeClass(c)">{{ couponStatusLabel(c) }}</span>
                </td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-light me-1" @click="openCouponModal(c)"><i class="fas fa-pen"></i></button>
                  <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteCoupon(c)"><i class="fas fa-trash"></i></button>
                </td>
              </tr>
              <tr v-if="coupons.length === 0">
                <td colspan="8" class="text-center text-muted py-4">No hay cupones creados</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <!-- Ofertas -->
    <div v-else-if="tab === 'offers'">
      <div class="panel-toolbar">
        <p class="panel-toolbar-meta">Descuentos temporales aplicados a productos del catálogo</p>
        <div class="panel-toolbar-actions">
          <button class="btn btn-primary" @click="openOfferModal()">
            <i class="fas fa-plus me-2"></i>Nueva oferta
          </button>
        </div>
      </div>

      <div v-if="loadingOffers" class="text-center py-5">
        <div class="spinner-border text-primary"></div>
      </div>
      <template v-else>
        <div class="panel-mobile-cards d-md-none">
          <article v-for="o in offers" :key="`m-o-${o.id}`" class="panel-data-card">
            <header class="panel-data-card__head">
              <p class="panel-data-card__title mb-0">{{ o.name }}</p>
              <span class="badge" :class="offerBadgeClass(o.status)">{{ offerStatusLabel(o.status) }}</span>
            </header>
            <div class="panel-data-card__rows">
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Descuento</span>
                <span class="panel-data-card__value fw-semibold">{{ o.discount }}%</span>
              </div>
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Roles</span>
                <span class="panel-data-card__value">{{ rolesLabel(o) }}</span>
              </div>
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Productos</span>
                <span class="panel-data-card__value">{{ offerProductsLabel(o) }}</span>
              </div>
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Inicio</span>
                <span class="panel-data-card__value">{{ formatDate(o.start_date) }}</span>
              </div>
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Fin</span>
                <span class="panel-data-card__value">{{ formatDate(o.end_date) }}</span>
              </div>
            </div>
            <footer class="panel-data-card__actions">
              <button class="btn btn-sm btn-outline-light" @click="openOfferModal(o)"><i class="fas fa-pen me-1"></i>Editar</button>
              <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteOffer(o)"><i class="fas fa-trash me-1"></i>Eliminar</button>
            </footer>
          </article>
          <div v-if="offers.length === 0" class="panel-data-card text-center text-muted py-4">No hay ofertas creadas</div>
        </div>
        <div class="table-responsive panel-table-wrap d-none d-md-block">
          <table class="table table-dark table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descuento</th>
                <th>Roles</th>
                <th>Productos</th>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="o in offers" :key="o.id">
                <td>{{ o.name }}</td>
                <td>{{ o.discount }}%</td>
                <td><small>{{ rolesLabel(o) }}</small></td>
                <td><small>{{ offerProductsLabel(o) }}</small></td>
                <td>{{ formatDate(o.start_date) }}</td>
                <td>{{ formatDate(o.end_date) }}</td>
                <td>
                  <span class="badge" :class="offerBadgeClass(o.status)">{{ offerStatusLabel(o.status) }}</span>
                </td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-light me-1" @click="openOfferModal(o)"><i class="fas fa-pen"></i></button>
                  <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteOffer(o)"><i class="fas fa-trash"></i></button>
                </td>
              </tr>
              <tr v-if="offers.length === 0">
                <td colspan="8" class="text-center text-muted py-4">No hay ofertas creadas</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <!-- Envío gratis -->
    <div v-else>
      <div class="alert alert-info panel-card mb-4">
        <i class="fas fa-info-circle me-2"></i>
        Reglas automáticas: si el subtotal de productos supera el monto mínimo, el envío queda en <strong>$0</strong>
        (sin código). Podés limitarlas por rol de cliente.
      </div>

      <div class="panel-toolbar">
        <p class="panel-toolbar-meta">Promociones de envío gratis por monto mínimo</p>
        <div class="panel-toolbar-actions">
          <button class="btn btn-primary" @click="openShippingModal()">
            <i class="fas fa-plus me-2"></i>Nueva promoción
          </button>
        </div>
      </div>

      <div v-if="loadingShipping" class="text-center py-5">
        <div class="spinner-border text-primary"></div>
      </div>
      <template v-else>
        <div class="panel-mobile-cards d-md-none">
          <article v-for="p in shippingPromos" :key="`m-s-${p.id}`" class="panel-data-card">
            <header class="panel-data-card__head">
              <p class="panel-data-card__title mb-0">{{ p.name }}</p>
              <span class="badge" :class="p.is_currently_active ? 'bg-success' : 'bg-secondary'">
                {{ p.is_currently_active ? 'Activa' : 'Inactiva' }}
              </span>
            </header>
            <div class="panel-data-card__rows">
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Mínimo</span>
                <span class="panel-data-card__value">{{ formatPrice(p.min_purchase) }}</span>
              </div>
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Roles</span>
                <span class="panel-data-card__value">{{ rolesLabel(p) }}</span>
              </div>
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Vigencia</span>
                <span class="panel-data-card__value">
                  {{ p.start_date ? formatDate(p.start_date) : '—' }}
                  →
                  {{ p.end_date ? formatDate(p.end_date) : '—' }}
                </span>
              </div>
            </div>
            <footer class="panel-data-card__actions">
              <button class="btn btn-sm btn-outline-light" @click="openShippingModal(p)"><i class="fas fa-pen me-1"></i>Editar</button>
              <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteShipping(p)"><i class="fas fa-trash me-1"></i>Eliminar</button>
            </footer>
          </article>
          <div v-if="shippingPromos.length === 0" class="panel-data-card text-center text-muted py-4">No hay promociones de envío gratis</div>
        </div>
        <div class="table-responsive panel-table-wrap d-none d-md-block">
          <table class="table table-dark table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Mínimo</th>
                <th>Roles</th>
                <th>Vigencia</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in shippingPromos" :key="p.id">
                <td>{{ p.name }}</td>
                <td>{{ formatPrice(p.min_purchase) }}</td>
                <td><small>{{ rolesLabel(p) }}</small></td>
                <td>
                  <small>
                    {{ p.start_date ? formatDate(p.start_date) : '—' }}
                    →
                    {{ p.end_date ? formatDate(p.end_date) : '—' }}
                  </small>
                </td>
                <td>
                  <span class="badge" :class="p.is_currently_active ? 'bg-success' : 'bg-secondary'">
                    {{ p.is_currently_active ? 'Activa' : 'Inactiva' }}
                  </span>
                </td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-light me-1" @click="openShippingModal(p)"><i class="fas fa-pen"></i></button>
                  <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteShipping(p)"><i class="fas fa-trash"></i></button>
                </td>
              </tr>
              <tr v-if="shippingPromos.length === 0">
                <td colspan="6" class="text-center text-muted py-4">No hay promociones de envío gratis</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <!-- Modal cupón -->
    <div
      v-if="showCouponModal"
      class="panel-modal-overlay"
      @pointerdown="couponOverlayDismiss.onOverlayPointerDown"
      @pointerup="couponOverlayDismiss.onOverlayPointerUp"
    >
      <div class="panel-modal">
        <h3>{{ editingCoupon ? 'Editar cupón' : 'Nuevo cupón' }}</h3>
        <form @submit.prevent="saveCoupon">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Código</label>
              <input v-model="couponForm.code" class="form-control" required :disabled="!!editingCoupon" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Tipo</label>
              <select v-model="couponForm.type" class="form-select">
                <option value="percentage">Porcentaje</option>
                <option value="fixed">Monto fijo</option>
                <option value="free_shipping">Envío gratis (con código)</option>
              </select>
            </div>
            <div v-if="couponForm.type !== 'free_shipping'" class="col-md-6">
              <label class="form-label">{{ couponForm.type === 'percentage' ? 'Porcentaje (%)' : 'Monto ($)' }}</label>
              <input v-model.number="couponForm.value" type="number" step="0.01" min="0" class="form-control" required />
            </div>
            <div class="col-md-6">
              <label class="form-label">Compra mínima ($)</label>
              <input v-model.number="couponForm.min_purchase" type="number" step="0.01" min="0" class="form-control" placeholder="Opcional" />
            </div>
            <div class="col-12">
              <RoleTargetingFields v-model="couponForm" :roles="roleOptions" id-prefix="coupon" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Máx. usos</label>
              <input v-model.number="couponForm.max_uses" type="number" min="1" class="form-control" placeholder="Ilimitado" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Vencimiento</label>
              <input v-model="couponForm.expires_at" type="datetime-local" class="form-control" required />
            </div>
            <div class="col-12">
              <div class="form-check">
                <input v-model="couponForm.active" class="form-check-input" type="checkbox" id="couponActive" />
                <label class="form-check-label" for="couponActive">Activo</label>
              </div>
            </div>
          </div>
          <div class="panel-modal-actions">
            <button type="button" class="btn btn-secondary" @click="showCouponModal = false">Cancelar</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Guardando...' : 'Guardar' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal oferta -->
    <div
      v-if="showOfferModal"
      class="panel-modal-overlay"
      @pointerdown="offerOverlayDismiss.onOverlayPointerDown"
      @pointerup="offerOverlayDismiss.onOverlayPointerUp"
    >
      <div class="panel-modal panel-modal-lg">
        <h3>{{ editingOffer ? 'Editar oferta' : 'Nueva oferta' }}</h3>
        <form @submit.prevent="saveOffer">
          <div class="row g-3">
            <div class="col-md-8">
              <label class="form-label">Nombre</label>
              <input v-model="offerForm.name" class="form-control" required />
            </div>
            <div class="col-md-4">
              <label class="form-label">Descuento (%)</label>
              <input v-model.number="offerForm.discount" type="number" step="0.01" min="0" max="100" class="form-control" required />
            </div>
            <div class="col-md-6">
              <label class="form-label">Inicio</label>
              <input v-model="offerForm.start_date" type="datetime-local" class="form-control" required />
            </div>
            <div class="col-md-6">
              <label class="form-label">Fin</label>
              <input v-model="offerForm.end_date" type="datetime-local" class="form-control" required />
            </div>
            <div class="col-12">
              <label class="form-label">Productos incluidos</label>
              <OfferProductPicker
                v-model="offerForm.products"
                :products="productOptions"
                input-id="offer-products"
              />
            </div>
            <div class="col-12">
              <RoleTargetingFields v-model="offerForm" :roles="roleOptions" id-prefix="offer" />
            </div>
          </div>
          <div class="panel-modal-actions">
            <button type="button" class="btn btn-secondary" @click="showOfferModal = false">Cancelar</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Guardando...' : 'Guardar' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal envío gratis -->
    <div
      v-if="showShippingModal"
      class="panel-modal-overlay"
      @pointerdown="shippingOverlayDismiss.onOverlayPointerDown"
      @pointerup="shippingOverlayDismiss.onOverlayPointerUp"
    >
      <div class="panel-modal">
        <h3>{{ editingShipping ? 'Editar promoción' : 'Nueva promoción de envío gratis' }}</h3>
        <form @submit.prevent="saveShipping">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Nombre</label>
              <input v-model="shippingForm.name" class="form-control" required placeholder="Ej: Envío gratis +$50.000" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Monto mínimo de compra ($)</label>
              <input v-model.number="shippingForm.min_purchase" type="number" step="0.01" min="0" class="form-control" required />
            </div>
            <div class="col-md-6">
              <label class="form-label">Inicio (opcional)</label>
              <input v-model="shippingForm.start_date" type="datetime-local" class="form-control" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Fin (opcional)</label>
              <input v-model="shippingForm.end_date" type="datetime-local" class="form-control" />
            </div>
            <div class="col-12">
              <RoleTargetingFields v-model="shippingForm" :roles="roleOptions" id-prefix="shipping" />
            </div>
            <div class="col-12">
              <div class="form-check">
                <input v-model="shippingForm.active" class="form-check-input" type="checkbox" id="shippingActive" />
                <label class="form-check-label" for="shippingActive">Activa</label>
              </div>
            </div>
          </div>
          <div class="panel-modal-actions">
            <button type="button" class="btn btn-secondary" @click="showShippingModal = false">Cancelar</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Guardando...' : 'Guardar' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useToast } from 'vue-toastification'
import RoleTargetingFields from '@/components/panel/RoleTargetingFields.vue'
import OfferProductPicker from '@/components/panel/OfferProductPicker.vue'
import { useModalOverlayDismiss } from '@/composables/useModalOverlayDismiss'
import { useUnsavedChangesGuard, useFormDirty } from '@/composables/useUnsavedChangesGuard'
import {
  getCoupons, createCoupon, updateCoupon, deleteCoupon,
  getOffers, createOffer, updateOffer, deleteOffer,
  getFreeShippingPromotions, createFreeShippingPromotion,
  updateFreeShippingPromotion, deleteFreeShippingPromotion,
} from '@/services/marketing'
import { getAllProductsForAdmin } from '@/services/products'
import { getIngresoCatalog } from '@/services/ingreso'
import { getCustomerRoles } from '@/services/invitations'

const toast = useToast()
const tab = ref('coupons')
const coupons = ref([])
const offers = ref([])
const shippingPromos = ref([])
const productOptions = ref([])
const roleOptions = ref([])
const loadingCoupons = ref(true)
const loadingOffers = ref(true)
const loadingShipping = ref(true)
const saving = ref(false)

const showCouponModal = ref(false)
const editingCoupon = ref(null)
const couponForm = ref(emptyCouponForm())
const couponOverlayDismiss = useModalOverlayDismiss(() => { showCouponModal.value = false })

const showOfferModal = ref(false)
const editingOffer = ref(null)
const offerForm = ref(emptyOfferForm())
const offerOverlayDismiss = useModalOverlayDismiss(() => { showOfferModal.value = false })

const showShippingModal = ref(false)
const editingShipping = ref(null)
const shippingForm = ref(emptyShippingForm())
const shippingOverlayDismiss = useModalOverlayDismiss(() => { showShippingModal.value = false })

function emptyRoleFields() {
  return { apply_to_all_roles: true, role_ids: [] }
}

function emptyCouponForm() {
  return { code: '', type: 'percentage', value: 10, min_purchase: null, max_uses: null, expires_at: '', active: true, ...emptyRoleFields() }
}

function emptyOfferForm() {
  return { name: '', discount: 10, start_date: '', end_date: '', products: [], ...emptyRoleFields() }
}

function emptyShippingForm() {
  return { name: '', min_purchase: 50000, active: true, start_date: '', end_date: '', ...emptyRoleFields() }
}

const couponDirty = useFormDirty(couponForm)
const offerDirty = useFormDirty(offerForm)
const shippingDirty = useFormDirty(shippingForm)

// Cambios sin guardar: solo cuentan mientras el modal correspondiente está abierto.
const dirty = computed(() =>
  (showCouponModal.value && couponDirty.dirty.value) ||
  (showOfferModal.value && offerDirty.dirty.value) ||
  (showShippingModal.value && shippingDirty.dirty.value)
)

const savePendingChanges = () => {
  if (showCouponModal.value) return saveCoupon()
  if (showOfferModal.value) return saveOffer()
  if (showShippingModal.value) return saveShipping()
  return Promise.resolve()
}

useUnsavedChangesGuard({
  dirty,
  saveFn: savePendingChanges,
  message: 'El formulario de promoción tiene cambios sin guardar. Si salís ahora, se van a perder.',
})

const couponTypeLabel = (t) => ({
  percentage: 'Porcentaje',
  fixed: 'Monto fijo',
  free_shipping: 'Envío gratis',
}[t] || t)

const rolesLabel = (item) => {
  if (item.apply_to_all_roles) return 'Todos'
  const names = (item.roles_detail || []).map(r => r.label)
  return names.length ? names.join(', ') : '—'
}

const formatPrice = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(n || 0)
const formatDate = (d) => d ? new Date(d).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '-'

const couponStatusLabel = (c) => {
  if (!c.active) return 'Inactivo'
  if (c.is_expired) return 'Vencido'
  if (c.max_uses && c.used >= c.max_uses) return 'Agotado'
  return 'Activo'
}
const couponBadgeClass = (c) => couponStatusLabel(c) === 'Activo' ? 'bg-success' : 'bg-secondary'

const offerStatusLabel = (s) => ({ active: 'Activa', pending: 'Programada', expired: 'Finalizada' }[s] || s)
const offerBadgeClass = (s) => ({ active: 'bg-success', pending: 'bg-warning text-dark', expired: 'bg-secondary' }[s] || 'bg-secondary')

const productNameById = computed(() => {
  const map = new Map()
  for (const p of productOptions.value) {
    map.set(Number(p.id), p.name)
  }
  return map
})

const offerProductsLabel = (offer) => {
  const ids = (offer.products || []).map((p) => (typeof p === 'object' ? p.id : p))
  if (!ids.length) return '—'
  const names = ids
    .map((id) => productNameById.value.get(Number(id)))
    .filter(Boolean)
  if (!names.length) return `${ids.length} producto(s)`
  if (names.length <= 2) return names.join(', ')
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`
}

const toLocalInput = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const toIso = (local) => local ? new Date(local).toISOString() : null

const loadCoupons = async () => {
  loadingCoupons.value = true
  try {
    const data = await getCoupons()
    coupons.value = Array.isArray(data) ? data : (data.results || [])
  } catch {
    toast.error('Error al cargar cupones')
  } finally {
    loadingCoupons.value = false
  }
}

const loadOffers = async () => {
  loadingOffers.value = true
  try {
    const data = await getOffers()
    offers.value = Array.isArray(data) ? data : (data.results || [])
  } catch {
    toast.error('Error al cargar ofertas')
  } finally {
    loadingOffers.value = false
  }
}

const loadProducts = async () => {
  try {
    const fromStore = await loadProductsFromIngresoStore()
    if (fromStore.length) {
      productOptions.value = fromStore
      return
    }
    productOptions.value = await loadProductsFromAdminCatalog()
  } catch {
    productOptions.value = []
  }
}

const loadProductsFromIngresoStore = async () => {
  const data = await getIngresoCatalog({ page_size: 500, active_only: 'true' })
  const items = data.results || []
  const byProduct = new Map()

  for (const item of items) {
    if (!item.product_id) continue
    const id = Number(item.product_id)
    if (!byProduct.has(id)) {
      byProduct.set(id, {
        id,
        name: item.product_detail?.name || item.product_name || `Producto #${id}`,
        type: item.product_detail?.type || item.product_type || '',
        image: item.product_image || item.product_detail?.images?.[0] || null,
        presentations: 0,
      })
    }
    byProduct.get(id).presentations += 1
  }

  return [...byProduct.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'))
}

const loadProductsFromAdminCatalog = async () => {
  let page = 1
  let all = []
  while (page <= 30) {
    const data = await getAllProductsForAdmin(true, page, 100)
    const batch = data.results || []
    all = all.concat(batch)
    if (!data.next || !batch.length) break
    page += 1
  }
  return all
    .filter((p) => p.type !== 'merch')
    .map((p) => ({
      id: p.id,
      name: p.name,
      type: p.type,
      image: p.image,
      presentations: p.blisters?.length || 0,
    }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'))
}

const loadRoles = async () => {
  try {
    const data = await getCustomerRoles({ active_only: 'true' })
    roleOptions.value = Array.isArray(data) ? data : (data.results || [])
  } catch {
    roleOptions.value = []
  }
}

const loadShippingPromos = async () => {
  loadingShipping.value = true
  try {
    const data = await getFreeShippingPromotions()
    shippingPromos.value = Array.isArray(data) ? data : (data.results || [])
  } catch {
    toast.error('Error al cargar promociones de envío')
  } finally {
    loadingShipping.value = false
  }
}

const mapRoleFieldsFromApi = (item) => ({
  apply_to_all_roles: item.apply_to_all_roles !== false,
  role_ids: (item.roles_detail || []).map(r => r.id),
})

const buildRolePayload = (form) => ({
  apply_to_all_roles: form.apply_to_all_roles,
  role_ids: form.apply_to_all_roles ? [] : (form.role_ids || []),
})

const openCouponModal = (coupon = null) => {
  editingCoupon.value = coupon
  couponForm.value = coupon
    ? {
        code: coupon.code,
        type: coupon.type,
        value: Number(coupon.value),
        min_purchase: coupon.min_purchase ? Number(coupon.min_purchase) : null,
        max_uses: coupon.max_uses,
        expires_at: toLocalInput(coupon.expires_at),
        active: coupon.active,
        ...mapRoleFieldsFromApi(coupon),
      }
    : emptyCouponForm()
  couponDirty.markClean()
  showCouponModal.value = true
}

const saveCoupon = async () => {
  if (!couponForm.value.apply_to_all_roles && !couponForm.value.role_ids?.length) {
    toast.error('Seleccioná al menos un rol o marcá "Todos los roles"')
    return
  }
  saving.value = true
  try {
    const payload = {
      ...couponForm.value,
      ...buildRolePayload(couponForm.value),
      expires_at: toIso(couponForm.value.expires_at),
      min_purchase: couponForm.value.min_purchase || null,
      max_uses: couponForm.value.max_uses || null,
      value: couponForm.value.type === 'free_shipping' ? 0 : couponForm.value.value,
    }
    if (editingCoupon.value) {
      await updateCoupon(editingCoupon.value.id, payload)
      toast.success('Cupón actualizado')
    } else {
      await createCoupon(payload)
      toast.success('Cupón creado')
    }
    showCouponModal.value = false
    await loadCoupons()
  } catch (e) {
    const err = e.response?.data
    toast.error(err?.code?.[0] || err?.detail || err?.value?.[0] || 'Error al guardar cupón')
  } finally {
    saving.value = false
  }
}

const confirmDeleteCoupon = async (coupon) => {
  if (!confirm(`¿Eliminar el cupón "${coupon.code}"?`)) return
  try {
    await deleteCoupon(coupon.id)
    toast.success('Cupón eliminado')
    await loadCoupons()
  } catch {
    toast.error('No se pudo eliminar el cupón')
  }
}

const openOfferModal = (offer = null) => {
  editingOffer.value = offer
  offerForm.value = offer
    ? {
        name: offer.name,
        discount: Number(offer.discount),
        start_date: toLocalInput(offer.start_date),
        end_date: toLocalInput(offer.end_date),
        products: (offer.products || []).map(p => (typeof p === 'object' ? p.id : p)),
        ...mapRoleFieldsFromApi(offer),
      }
    : emptyOfferForm()
  offerDirty.markClean()
  showOfferModal.value = true
}

const saveOffer = async () => {
  if (!offerForm.value.products.length) {
    toast.error('Seleccioná al menos un producto')
    return
  }
  if (!offerForm.value.apply_to_all_roles && !offerForm.value.role_ids?.length) {
    toast.error('Seleccioná al menos un rol o marcá "Todos los roles"')
    return
  }
  saving.value = true
  try {
    const payload = {
      name: offerForm.value.name,
      discount: offerForm.value.discount,
      start_date: toIso(offerForm.value.start_date),
      end_date: toIso(offerForm.value.end_date),
      products: offerForm.value.products.map(Number),
      ...buildRolePayload(offerForm.value),
    }
    if (editingOffer.value) {
      await updateOffer(editingOffer.value.id, payload)
      toast.success('Oferta actualizada')
    } else {
      await createOffer(payload)
      toast.success('Oferta creada')
    }
    showOfferModal.value = false
    await loadOffers()
  } catch (e) {
    const err = e.response?.data
    toast.error(err?.products?.[0] || err?.detail || err?.discount?.[0] || err?.end_date?.[0] || 'Error al guardar oferta')
  } finally {
    saving.value = false
  }
}

const confirmDeleteOffer = async (offer) => {
  if (!confirm(`¿Eliminar la oferta "${offer.name}"?`)) return
  try {
    await deleteOffer(offer.id)
    toast.success('Oferta eliminada')
    await loadOffers()
  } catch {
    toast.error('No se pudo eliminar la oferta')
  }
}

const openShippingModal = (promo = null) => {
  editingShipping.value = promo
  shippingForm.value = promo
    ? {
        name: promo.name,
        min_purchase: Number(promo.min_purchase),
        active: promo.active,
        start_date: toLocalInput(promo.start_date),
        end_date: toLocalInput(promo.end_date),
        ...mapRoleFieldsFromApi(promo),
      }
    : emptyShippingForm()
  shippingDirty.markClean()
  showShippingModal.value = true
}

const saveShipping = async () => {
  if (!shippingForm.value.apply_to_all_roles && !shippingForm.value.role_ids?.length) {
    toast.error('Seleccioná al menos un rol o marcá "Todos los roles"')
    return
  }
  saving.value = true
  try {
    const payload = {
      name: shippingForm.value.name,
      min_purchase: shippingForm.value.min_purchase,
      active: shippingForm.value.active,
      start_date: toIso(shippingForm.value.start_date),
      end_date: toIso(shippingForm.value.end_date),
      ...buildRolePayload(shippingForm.value),
    }
    if (editingShipping.value) {
      await updateFreeShippingPromotion(editingShipping.value.id, payload)
      toast.success('Promoción actualizada')
    } else {
      await createFreeShippingPromotion(payload)
      toast.success('Promoción creada')
    }
    showShippingModal.value = false
    await loadShippingPromos()
  } catch (e) {
    const err = e.response?.data
    toast.error(err?.role_ids?.[0] || err?.detail || err?.min_purchase?.[0] || 'Error al guardar promoción')
  } finally {
    saving.value = false
  }
}

const confirmDeleteShipping = async (promo) => {
  if (!confirm(`¿Eliminar la promoción "${promo.name}"?`)) return
  try {
    await deleteFreeShippingPromotion(promo.id)
    toast.success('Promoción eliminada')
    await loadShippingPromos()
  } catch {
    toast.error('No se pudo eliminar la promoción')
  }
}

onMounted(async () => {
  await Promise.all([loadCoupons(), loadOffers(), loadShippingPromos(), loadProducts(), loadRoles()])
})
</script>

<style scoped>
.nav-tabs .nav-link {
  color: rgba(255, 255, 255, 0.65);
  border: none;
  background: transparent;
  cursor: pointer;
}
.nav-tabs .nav-link.active {
  color: var(--primary-color);
  border-bottom: 2px solid var(--primary-color);
  background: transparent;
}
.panel-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 1rem;
}
.panel-modal {
  background: var(--grey-color);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  padding: 1.5rem;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
}
.panel-modal-lg { max-width: 640px; }
.panel-modal h3 { font-size: 1.15rem; margin-bottom: 1.25rem; color: var(--white-color); }
.panel-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}
.form-label { color: rgba(255, 255, 255, 0.85); font-size: 0.85rem; }
</style>
